import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/store';
import AppLayout from './layout/AppLayout';
import LoginView from './views/LoginView';
import AccountSelectionView from './views/AccountSelectionView';
import ForgotPasswordView from './views/ForgotPasswordView';
import NewPasswordView from './views/NewPasswordView';
import {
  setAuthView,
  setAuthentication,
  setIsAdmin,
} from '../store/appSlice';
import { fetchAuthSession } from 'aws-amplify/auth';
import { CircularProgress, Box } from '@mui/material';
import { init as initAmplitude, setUserId as setAmplitudeUserId } from '../services/amplitude';

const AppRouter: React.FC = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, isAdmin, selectedAccount, authView } = useSelector((state: RootState) => state.app);
  const [authChecked, setAuthChecked] = useState(false);
  const [needsNewPassword, setNeedsNewPassword] = useState(false);

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken;
        // init AFTER auth fetch
        initAmplitude();

        if (idToken) {
          // Sync admin status with token
          const userIsAdmin = idToken.payload['custom:isAdmin'] === 'true';
          dispatch(setIsAdmin(userIsAdmin));
          dispatch(setAuthentication(true));

          // Set Amplitude user ID from email
          const userEmail = idToken.payload.email;
          if (userEmail && typeof userEmail === 'string') {
            setAmplitudeUserId(userEmail);
          }
        } else {
          // No token - user not authenticated
          dispatch(setAuthentication(false));
          dispatch(setIsAdmin(false));
        }
      } catch {
        // Session fetch failed - user not authenticated
        initAmplitude();
        dispatch(setAuthentication(false));
        dispatch(setIsAdmin(false));
      } finally {
        setAuthChecked(true);
      }
    };
    checkAuthStatus();
  }, [dispatch]);

  if (!authChecked) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (needsNewPassword) {
    return <NewPasswordView onSuccess={() => setNeedsNewPassword(false)} />;
  }

  // Debug logging
  console.log('AppRouter state:', { isAuthenticated, isAdmin, selectedAccount, authView });

  // Show forgot password view if not authenticated and authView is forgot-password
  if (!isAuthenticated && authView === 'forgot-password') {
    return <ForgotPasswordView onBackToLogin={() => dispatch(setAuthView('login'))} />;
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    console.log('Showing LoginView - not authenticated');
    return <LoginView onNewPasswordRequired={() => setNeedsNewPassword(true)} />;
  }

  // Show account selection if admin user hasn't selected an account
  if (isAdmin && !selectedAccount) {
    return <AccountSelectionView />;
  }

  // Show main app layout
  return <AppLayout />;
};

export default AppRouter;
